import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Crown, TrendingUp, Lightbulb, Award } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { useTranslation } from 'react-i18next';

interface PremiumInsightsProps {
  insights: {
    categories?: {
      lighting: number;
      ornaments: number;
      symmetry: number;
      creativity: number;
    };
    trends?: string[];
    suggestions?: string[];
    comparison?: string;
  };
}

const PremiumInsights = ({ insights }: PremiumInsightsProps) => {
  const { t } = useTranslation();
  const categories = insights.categories || {
    lighting: 0,
    ornaments: 0,
    symmetry: 0,
    creativity: 0,
  };

  const categoryIcons = {
    lighting: '💡',
    ornaments: '🎄',
    symmetry: '⚖️',
    creativity: '✨',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Crown className="h-5 w-5 text-yellow-500" />
        <h3 className="text-xl font-bold">{t('premiumInsights.title')}</h3>
        <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500">{t('premiumInsights.unlocked')}</Badge>
      </div>

      {/* Category Scores */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            {t('premiumInsights.detailedAnalysis')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(categories).map(([category, score]) => (
            <div key={category} className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium capitalize flex items-center gap-2">
                  <span>{categoryIcons[category as keyof typeof categoryIcons]}</span>
                  {t(`premiumInsights.categories.${category}`)}
                </span>
                <span className="text-sm font-bold">{score}/10</span>
              </div>
              <Progress value={score * 10} className="h-2" />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Trends */}
      {insights.trends && insights.trends.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              {t('premiumInsights.decorationTrends')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {insights.trends.map((trend, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span className="text-sm text-muted-foreground">{trend}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Suggestions */}
      {insights.suggestions && insights.suggestions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5" />
              {t('premiumInsights.improvementSuggestions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {insights.suggestions.map((suggestion, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-primary mt-1">💡</span>
                  <span className="text-sm text-muted-foreground">{suggestion}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Comparison */}
      {insights.comparison && (
        <Card>
          <CardHeader>
            <CardTitle>{t('premiumInsights.yourRanking')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{insights.comparison}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PremiumInsights;
